import { useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Linkedin, Users, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExpertSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isExpert, user } = useAuth();
  
  const error = searchParams.get('error');
  const refToken = searchParams.get('ref');

  // Persist referral token so it survives the LinkedIn OAuth round-trip
  useEffect(() => {
    if (refToken) {
      try { localStorage.setItem('collabhive_uni_ref', refToken); } catch (e) { /* noop */ }
    }
  }, [refToken]);

  useEffect(() => {
    // If already logged in as expert, redirect to dashboard
    if (isAuthenticated && isExpert) {
      if (user?.orcid_id) {
        navigate('/expert/dashboard');
      } else {
        navigate('/expert/complete-profile');
      }
    }
  }, [isAuthenticated, isExpert, user, navigate]);

  const handleLinkedInSignup = () => {
    window.location.href = `${BACKEND_URL}/api/auth/linkedin/login`;
  };

  const requirements = [
    "Valid LinkedIn account with professional profile",
    "ORCID ID for academic verification",
    "Lab access for research capabilities",
    "Connections with other experts",
    "Previous industry collaboration experience"
  ];

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-semibold tracking-tight">CollabHive</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
            </div>
          </Link>
          
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      <div className="container-main py-16">
        <div className="max-w-2xl mx-auto">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mb-6 animate-fade-in">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                {error === 'token_exchange_failed' && 'Failed to authenticate with LinkedIn. Please try again.'}
                {error === 'profile_fetch_failed' && 'Could not retrieve your LinkedIn profile. Please try again.'}
                {error === 'no_code' && 'Authorization was cancelled. Please try again.'}
                {!['token_exchange_failed', 'profile_fetch_failed', 'no_code'].includes(error) && `Error: ${error}`}
              </AlertDescription>
            </Alert>
          )}

          <Card className="animate-fade-in">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-[#0077b5]/10 flex items-center justify-center mx-auto mb-4">
                <Linkedin className="w-8 h-8 text-[#0077b5]" />
              </div>
              <CardTitle className="text-2xl">Join as an Expert</CardTitle>
              <CardDescription className="text-base">
                Sign up with LinkedIn to create your expert profile on CollabHive
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6 pt-4">
              {/* Requirements */}
              <div className="bg-muted/50 rounded-lg p-6">
                <h3 className="font-semibold mb-4">Requirements for Approval</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  To ensure quality, experts must meet all three criteria to be visible to industry users:
                </p>
                <ul className="space-y-3">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Signup Button */}
              <Button 
                onClick={handleLinkedInSignup}
                className="w-full btn-linkedin gap-3 h-12 text-base"
                data-testid="linkedin-signup-btn"
              >
                <Linkedin className="w-5 h-5" />
                Continue with LinkedIn
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                By signing up, you agree to our terms of service and privacy policy. 
                Your LinkedIn profile information will be used to create your expert profile.
              </p>

              {/* Already have account */}
              <div className="text-center pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Looking for expertise instead?{' '}
                  <Link to="/search" className="text-primary hover:underline font-medium">
                    Search Experts
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
